/**
 * Single-PUT upload to a presigned URL with parallel SHA-256 computation.
 *
 * R2 (and S3) accept a single PUT for objects up to 5 GB. A 3-batch take at
 * 48 kHz / 24-bit mono is ~50 MB — comfortably below that — so V1 uses a
 * single PUT. The `chunkSize` parameter is reserved for a future multipart
 * implementation; today it controls the granularity of progress callbacks
 * by feeding the SHA-256 digest in slices, but the network PUT is one shot.
 *
 * SHA-256 is computed via SubtleCrypto.digest over the full payload (we only
 * have one shot at upload, so streaming digest isn't required). The server
 * verifies the returned hash against its own digest of the stored object.
 */

export interface ChunkedUploadOptions {
  /** Presigned PUT URL from the API. Single-use. */
  presignedUrl: string
  /** Payload to upload. Either a typed-array view or a Blob. */
  data: Uint8Array | Blob
  /**
   * Default 256 KiB. Used to slice the payload for incremental progress
   * reporting; not the network chunk size (V1 is single PUT).
   */
  chunkSize?: number
  /** Called periodically with (bytesSent, totalBytes). */
  onProgress?: (sent: number, total: number) => void
  /** Aborts both the digest and the PUT. */
  signal?: AbortSignal
  /** MIME type sent in the Content-Type header. */
  contentType?: string
}

export interface ChunkedUploadResult {
  /** ETag header returned by the storage backend (quoted as received). */
  etag: string
  /** Lowercase hex SHA-256 of the uploaded bytes. */
  sha256: string
}

const DEFAULT_CHUNK_SIZE = 256 * 1024

/**
 * Upload `data` to `presignedUrl` via PUT, computing SHA-256 in lockstep so
 * we can return both the storage ETag and the content hash for server-side
 * verification. Throws on non-2xx response or aborted signal.
 */
export async function chunkedUpload(opts: ChunkedUploadOptions): Promise<ChunkedUploadResult> {
  const bytes = await toUint8Array(opts.data)
  const total = bytes.byteLength

  // Hash the payload up front. We deliberately await this BEFORE the PUT so
  // a network failure doesn't leave us with an unhashed orphan. SubtleCrypto
  // is fast on modern engines (~500 MB/s) — a 50 MB take hashes in ~100 ms.
  const sha256 = await sha256Hex(bytes, opts)

  // Report incremental progress in chunkSize-byte ticks before sending. This
  // doesn't reflect actual network progress (fetch() doesn't expose upload
  // progress in most browsers), but it's better than a single 0→100 jump
  // for the UI. Real progress streaming will land with the resumable
  // multipart upgrade.
  if (opts.onProgress) {
    const chunkSize = opts.chunkSize ?? DEFAULT_CHUNK_SIZE
    let sent = 0
    while (sent < total) {
      sent = Math.min(total, sent + chunkSize)
      opts.onProgress(sent, total)
      if (opts.signal?.aborted) throw new DOMException('Upload aborted', 'AbortError')
    }
  }

  const response = await fetch(opts.presignedUrl, {
    method: 'PUT',
    body: bytes,
    headers: {
      'Content-Type': opts.contentType ?? 'application/octet-stream',
    },
    signal: opts.signal,
  })

  if (!response.ok) {
    throw new Error(`chunkedUpload: PUT failed with ${response.status} ${response.statusText}`)
  }

  const etag = response.headers.get('etag') ?? ''
  return { etag, sha256 }
}

async function toUint8Array(data: Uint8Array | Blob): Promise<Uint8Array> {
  if (data instanceof Uint8Array) return data
  const buf = await data.arrayBuffer()
  return new Uint8Array(buf)
}

/** Lowercase hex SHA-256 of `bytes` via SubtleCrypto. */
export async function sha256Hex(
  bytes: Uint8Array,
  opts: { signal?: AbortSignal } = {},
): Promise<string> {
  if (opts.signal?.aborted) throw new DOMException('Aborted', 'AbortError')
  // Slice into a fresh ArrayBuffer to be safe — Uint8Array views may have a
  // non-zero byteOffset which crypto.subtle.digest historically mishandled.
  const ab =
    bytes.byteOffset === 0 && bytes.byteLength === bytes.buffer.byteLength
      ? bytes.buffer
      : bytes.slice().buffer
  const digest = await crypto.subtle.digest('SHA-256', ab)
  return bytesToHex(new Uint8Array(digest))
}

function bytesToHex(b: Uint8Array): string {
  let hex = ''
  for (let i = 0; i < b.length; i++) {
    const byte = b[i] ?? 0
    hex += byte.toString(16).padStart(2, '0')
  }
  return hex
}
